import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../hooks/useApi';
import DateInput from './DateInput';
import Icon from './Icon';
import './EnteteBatch.css';

/**
 * EnteteBatch — composant partagé pour l'étape 1 de toutes les pages de production.
 *
 * Deux modes :
 * - Création (batch == null) : collecte les champs communs + champsSpecifiques (children).
 *   Sur « Créer le batch », appelle onCreated({ batchId, batchData }) via le callback fourni.
 * - Édition (batch != null) : pré-rempli depuis batch, auto-save sur blur via PUT /<apiBase>/batches/:id.
 *   Les champs spécifiques au produit sont rendus via children (injectés par la page).
 *
 * Props :
 *   apiBase          {string}   ex. '/sauce', '/tallow', '/porc', '/saucisses'
 *   produitCode      {string}   ex. 'sauce', 'tallow', 'porc' — pour GET /batches/prochain-numero/:produit
 *   batch            {object|null}  batch existant ou null pour création
 *   onCreated        {function} appelé après POST avec { batchId, batchData }
 *   onUpdated        {function} appelé après PUT avec le batch mis à jour (optionnel)
 *   children         {ReactNode} champs spécifiques au produit (rendus après les champs communs)
 *   labelBoutonCreer {string}   label du bouton de création (défaut: 'Créer le batch →')
 *   submitRef        {ref}      ref externe pour déclencher la soumission (optionnel, mode création)
 *   extraCreationFields {object} champs supplémentaires à envoyer au POST (ex. { multiplicateur: 1 })
 */
export default function EnteteBatch({
  apiBase,
  produitCode,
  batch,
  onCreated,
  onUpdated,
  children,
  labelBoutonCreer = 'Créer le batch →',
  submitRef,
  extraCreationFields = {},
}) {
  const modeCreation = !batch;
  const isFinalise   = batch?.statut === 'finalise';

  // ─── État commun ─────────────────────────────────────────────────────────

  const [nomAuto, setNomAuto]       = useState('');
  const [nomManuel, setNomManuel]   = useState(false);
  const [form, setForm]             = useState(() => initialForm(batch));
  const [loading, setLoading]       = useState(false);
  const [erreur, setErreur]         = useState('');
  const [toast, setToast]           = useState('');
  const submittingRef               = useRef(false);

  function initialForm(b) {
    return {
      nom                      : b?.nom                       || '',
      date_prod                : b?.date_prod                 || new Date().toISOString().split('T')[0],
      operateurs               : b?.operateurs                || '',
      fournisseur              : b?.fournisseur               || '',
      lot_fournisseur          : b?.lot_fournisseur           || '',
      date_achat_matiere       : b?.date_achat_matiere        || '',
      date_expiration_matiere  : b?.date_expiration_matiere   || '',
      heure_debut              : b?.heure_debut               || '',
      heure_fin                : b?.heure_fin                 || '',
      notes                    : b?.notes                     || '',
    };
  }

  const set = useCallback((k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
  }, []);

  // ─── Mode création : charger le numéro auto ───────────────────────────────

  useEffect(() => {
    if (!modeCreation) return;
    if (!produitCode) return;
    api.get(`/batches/prochain-numero/${produitCode}`)
      .then(r => {
        setNomAuto(r.nom);
        setForm(prev => ({ ...prev, nom: prev.nom || r.nom }));
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // ─── Mode édition : sync si le batch change depuis l'extérieur ──────────

  useEffect(() => {
    if (!batch) return;
    setForm(initialForm(batch));
  }, [batch?.id]); // eslint-disable-line

  // ─── Auto-save (mode édition) sur blur ───────────────────────────────────

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const sauvegarderChamp = useCallback(async (champForm) => {
    if (modeCreation) return;
    if (!batch?.id) return;
    const payload = {
      nom                   : champForm.nom                    || undefined,
      date_prod             : champForm.date_prod              || undefined,
      operateurs            : champForm.operateurs             || undefined,
      fournisseur           : champForm.fournisseur            || undefined,
      lot_fournisseur       : champForm.lot_fournisseur        || undefined,
      date_achat_matiere    : champForm.date_achat_matiere     || undefined,
      date_expiration_matiere: champForm.date_expiration_matiere || undefined,
      heure_debut           : champForm.heure_debut            || undefined,
      heure_fin             : champForm.heure_fin              || undefined,
      notes                 : champForm.notes                  || undefined,
    };
    try {
      const updated = await api.put(`${apiBase}/batches/${batch.id}`, payload);
      if (onUpdated) onUpdated(updated);
      showToast('Sauvegarde effectuée');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Erreur de sauvegarde');
    }
  }, [modeCreation, batch?.id, apiBase, onUpdated]); // eslint-disable-line

  const saveAll = useCallback(() => sauvegarderChamp(form), [form, sauvegarderChamp]);
  if (submitRef) submitRef.current = saveAll;

  // ─── Création ────────────────────────────────────────────────────────────

  async function handleCreer(e) {
    if (e?.preventDefault) e.preventDefault();
    if (!form.operateurs) { setErreur('Veuillez sélectionner les opérateurs.'); return; }
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setLoading(true);
    setErreur('');
    try {
      const nomEffectif = form.nom.trim() || nomAuto || undefined;
      const payload = {
        nom                     : nomEffectif,
        date_prod               : form.date_prod                || undefined,
        operateurs              : form.operateurs               || undefined,
        fournisseur             : form.fournisseur              || undefined,
        lot_fournisseur         : form.lot_fournisseur          || undefined,
        date_achat_matiere      : form.date_achat_matiere       || undefined,
        date_expiration_matiere : form.date_expiration_matiere  || undefined,
        heure_debut             : form.heure_debut              || undefined,
        heure_fin               : form.heure_fin                || undefined,
        notes                   : form.notes                    || undefined,
        ...extraCreationFields,
      };
      const res = await api.post(`${apiBase}/batches`, payload);
      onCreated({ batchId: res.id, batchData: res });
    } catch (err) {
      setErreur(err.response?.data?.error || err.message || 'Erreur lors de la création du batch');
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }

  // ─── Rendu ───────────────────────────────────────────────────────────────

  return (
    <div className="entete-batch">
      {toast && (
        <div className="entete-batch-toast">
          <Icon name="check" size={14} weight="bold" />
          {toast}
        </div>
      )}

      {erreur && (
        <div className="alert-danger">
          <Icon name="alerte" size={15} />
          {erreur}
        </div>
      )}

      {/* En-tête mode édition */}
      {!modeCreation && (
        <div className="entete-batch-titre">
          <span className="batch-nom">{batch.nom}</span>
          {isFinalise ? (
            <span className="badge badge--success">Finalisé</span>
          ) : (
            <span className="badge badge--warning">En cours</span>
          )}
          {isFinalise && (
            <p className="entete-batch-note">
              Batch finalisé — la date, les opérateurs et les notes restent éditables.
            </p>
          )}
        </div>
      )}

      <div className="form-grid">
        {/* Numéro du batch */}
        <div className="form-group">
          <label>Numéro du batch</label>
          {modeCreation ? (
            <div className="num-batch-row">
              <input
                className="form-input"
                value={form.nom}
                onChange={e => set('nom', e.target.value)}
                readOnly={!nomManuel}
                placeholder={nomAuto || 'Chargement…'}
              />
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                onClick={() => setNomManuel(m => !m)}
              >
                {nomManuel ? (
                  <><Icon name="retour" size={13} /> Auto</>
                ) : (
                  <><Icon name="modifier" size={13} /> Modifier</>
                )}
              </button>
            </div>
          ) : (
            <input
              className="form-input"
              value={form.nom}
              onChange={e => set('nom', e.target.value)}
              onBlur={() => sauvegarderChamp(form)}
              disabled={isFinalise}
            />
          )}
          <span className="field-hint">Numéro auto-généré</span>
        </div>

        {/* Date de production */}
        <div className="form-group">
          <label>Date de production *</label>
          <DateInput
            className="form-input"
            value={form.date_prod}
            onChange={v => {
              set('date_prod', v);
              if (!modeCreation) sauvegarderChamp({ ...form, date_prod: v });
            }}
          />
        </div>

        {/* Opérateurs */}
        <div className="form-group">
          <label>Opérateurs *</label>
          <select
            className="form-input"
            value={form.operateurs}
            onChange={e => set('operateurs', e.target.value)}
            onBlur={() => sauvegarderChamp(form)}
            required
          >
            <option value="">— Sélectionner —</option>
            <option value="LCU">LCU</option>
            <option value="ELA">ELA</option>
            <option value="LCU+ELA">Les deux</option>
          </select>
        </div>

        {/* Fournisseur */}
        <div className="form-group">
          <label>Fournisseur</label>
          <input
            className="form-input"
            value={form.fournisseur}
            onChange={e => set('fournisseur', e.target.value)}
            onBlur={() => sauvegarderChamp(form)}
            placeholder="ex : Viandes Gosselin, Costco…"
          />
        </div>

        {/* Lot fournisseur */}
        <div className="form-group">
          <label>Numéro de lot fournisseur</label>
          <input
            className="form-input"
            value={form.lot_fournisseur}
            onChange={e => set('lot_fournisseur', e.target.value)}
            onBlur={() => sauvegarderChamp(form)}
            placeholder="ex : LOT-2026-0342"
          />
        </div>

        {/* Date achat matière */}
        <div className="form-group">
          <label>Date d'achat matière première</label>
          <DateInput
            className="form-input"
            value={form.date_achat_matiere}
            onChange={v => {
              set('date_achat_matiere', v);
              if (!modeCreation) sauvegarderChamp({ ...form, date_achat_matiere: v });
            }}
          />
        </div>

        {/* Date expiration matière */}
        <div className="form-group">
          <label>Date d'expiration matière première</label>
          <DateInput
            className="form-input"
            value={form.date_expiration_matiere}
            onChange={v => {
              set('date_expiration_matiere', v);
              if (!modeCreation) sauvegarderChamp({ ...form, date_expiration_matiere: v });
            }}
          />
        </div>

        {/* Heure début */}
        <div className="form-group">
          <label>Heure de début</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_debut}
            onChange={e => set('heure_debut', e.target.value)}
            onBlur={() => sauvegarderChamp(form)}
          />
        </div>

        {/* Heure fin */}
        <div className="form-group">
          <label>Heure de fin</label>
          <input
            className="form-input"
            type="time"
            value={form.heure_fin}
            onChange={e => set('heure_fin', e.target.value)}
            onBlur={() => sauvegarderChamp(form)}
          />
        </div>
      </div>

      {/* Notes */}
      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows="2"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          onBlur={() => sauvegarderChamp(form)}
          placeholder="Notes de production, qualité, observations…"
        />
      </div>

      {/* Champs spécifiques au produit — injectés par la page */}
      {children && (
        <div className="entete-batch-specifiques">
          {children}
        </div>
      )}

      {/* Bouton création seulement */}
      {modeCreation && (
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreer}
            disabled={loading || !form.operateurs}
          >
            {loading ? 'Création…' : labelBoutonCreer}
          </button>
        </div>
      )}
    </div>
  );
}
